<script setup lang="ts">
import {
  CUSTOM_IMAGE_ASPECT_RATIO,
  IMAGE_ASPECT_RATIO_PRESETS,
  normalizeImageFocalPosition,
  type ImageFit,
} from "#bb/lib/image";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);

const source = field<string>("source", "");
const alt = field<string>("alt", "");
const decorative = field<boolean>("decorative", false);
const aspectRatio = field<string>("aspectRatio", "16:9");
const customRatioWidth = field<number>("customRatioWidth", 16);
const customRatioHeight = field<number>("customRatioHeight", 9);
const fit = field<ImageFit>("fit", "cover");
const focalX = field<number>("focalX", 50);
const focalY = field<number>("focalY", 50);

const focalXModel = computed({
  get: () => [normalizeImageFocalPosition(focalX.value)],
  set: (value: number[] | undefined) => {
    focalX.value = normalizeImageFocalPosition(value?.[0]);
  },
});
const focalYModel = computed({
  get: () => [normalizeImageFocalPosition(focalY.value)],
  set: (value: number[] | undefined) => {
    focalY.value = normalizeImageFocalPosition(value?.[0]);
  },
});
</script>

<template>
  <EditorSection :title="t('imageSection')" icon="lucide:image" accent="media">
    <EditorField :label="t('sourceLabel')" :hint="t('sourceHint')">
      <EditorPathInput
        v-model="source"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        content-type-filter="image"
        :placeholder="t('sourcePlaceholder')"
        :aria-label="t('sourceLabel')"
      />
    </EditorField>

    <EditorField :label="t('decorativeLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("decorativeHint") }}</span>
        <Switch v-model="decorative" />
      </div>
    </EditorField>

    <EditorField
      v-if="!decorative"
      :label="t('altLabel')"
      :hint="t('altHint')"
    >
      <Input
        v-model="alt"
        class="h-8 rounded-sm bg-muted text-[13px]"
        :placeholder="t('altPlaceholder')"
      />
    </EditorField>
  </EditorSection>

  <EditorSection
    :title="t('layoutSection')"
    icon="lucide:scan"
    accent="media"
  >
    <EditorField :label="t('ratioLabel')" :hint="t('ratioHint')">
      <Select
        :model-value="aspectRatio"
        @update:model-value="(value) => (aspectRatio = String(value))"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in IMAGE_ASPECT_RATIO_PRESETS"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
          <SelectItem :value="CUSTOM_IMAGE_ASPECT_RATIO">
            {{ t("customRatio") }}
          </SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField
      v-if="aspectRatio === CUSTOM_IMAGE_ASPECT_RATIO"
      :label="t('customRatioLabel')"
      :hint="t('customRatioHint')"
    >
      <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <Input
          v-model.number="customRatioWidth"
          type="number"
          min="0.01"
          step="0.01"
          class="h-8 rounded-sm bg-muted text-[13px]"
          :aria-label="t('ratioWidth')"
        />
        <span class="text-muted-foreground">:</span>
        <Input
          v-model.number="customRatioHeight"
          type="number"
          min="0.01"
          step="0.01"
          class="h-8 rounded-sm bg-muted text-[13px]"
          :aria-label="t('ratioHeight')"
        />
      </div>
    </EditorField>

    <EditorField :label="t('fitLabel')" :hint="t('fitHint')">
      <Select
        :model-value="fit"
        @update:model-value="(value) => (fit = value as ImageFit)"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="cover">{{ t("cover") }}</SelectItem>
          <SelectItem value="contain">{{ t("contain") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>
  </EditorSection>

  <EditorSection
    v-if="fit === 'cover'"
    :title="t('focalSection')"
    icon="lucide:crosshair"
    accent="media"
    :default-open="false"
  >
    <EditorField :label="t('focalXLabel')" :hint="t('focalXHint')">
      <div class="flex items-center gap-3">
        <Slider v-model="focalXModel" :min="0" :max="100" :step="1" class="flex-1" />
        <span class="w-10 text-right font-mono text-[12px] text-muted-foreground">
          {{ focalXModel[0] }}%
        </span>
      </div>
    </EditorField>

    <EditorField :label="t('focalYLabel')" :hint="t('focalYHint')">
      <div class="flex items-center gap-3">
        <Slider v-model="focalYModel" :min="0" :max="100" :step="1" class="flex-1" />
        <span class="w-10 text-right font-mono text-[12px] text-muted-foreground">
          {{ focalYModel[0] }}%
        </span>
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  imageSection: "Image"
  sourceLabel: "Image"
  sourceHint: "Choose a site file or use an HTTP(S) image URL."
  sourcePlaceholder: "/media/example.jpg"
  decorativeLabel: "Decorative image"
  decorativeHint: "Hide this image from screen readers."
  altLabel: "Alternative text"
  altHint: "Describe the image's purpose or content for screen-reader users."
  altPlaceholder: "A concise description of the image"
  layoutSection: "Layout"
  ratioLabel: "Aspect ratio"
  ratioHint: "The frame keeps this proportion at every responsive width."
  customRatio: "Custom"
  customRatioLabel: "Custom ratio"
  customRatioHint: "Enter positive width and height proportions."
  ratioWidth: "Ratio width"
  ratioHeight: "Ratio height"
  fitLabel: "Image fit"
  fitHint: "Cover fills the frame by cropping; Contain shows the entire image."
  cover: "Cover"
  contain: "Contain"
  focalSection: "Cover focal point"
  focalXLabel: "Horizontal position"
  focalXHint: "Choose which horizontal area remains visible when cropped."
  focalYLabel: "Vertical position"
  focalYHint: "Choose which vertical area remains visible when cropped."
</i18n>
